import cx from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import { FunctionComponent } from 'react';

import IconWallet from '../../icons/wallet';

import classes from './accumulation-label.module.css';

export type TAccumulationLabelProps = {
  /** Already-formatted amount, e.g. `€ 9.36`. */
  amount: string;
  /**
   * `BEFORE` a purchase the label reads "acumula" (what the customer will earn),
   * `AFTER` it reads "acumulou" (what was earned) — same label, past tense.
   */
  tense?: 'BEFORE' | 'AFTER';
  /**
   * How the accumulation is named: `TEXT` spells it out ("Acumula"/"Acumulou",
   * per `tense`), `PLUS` shortens it to a `+` for tight spots where the word
   * does not fit next to the amount. `PLUS` ignores `tense` — a sign has none.
   */
  label?: 'TEXT' | 'PLUS';
  /**
   * `TAG` is the blue house-shaped badge that sits over product images.
   * `INLINE` is the one-line "wallet icon + sentence + amount" row for summaries
   * (cart, checkout, order pages) — it takes the full width it is given.
   */
  variant?: 'TAG' | 'INLINE';
  /** `SM` fits a listing card, `MD` a summary line, `LG` a standalone banner. */
  size?: 'SM' | 'MD';
  /**
   * `TAG` only: background utility. Defaults to the design's fixed B-Parts blue
   * (identical in every tenant palette); pass `bg-brand-100` to follow the brand.
   */
  background?: string;
  /** `INLINE` only: text colour utility. */
  color?: string;
  className?: string;
};

const INLINE_SIZES: {
  [key in NonNullable<TAccumulationLabelProps['size']>]: {
    icon: string;
    label: string;
    amount: string;
    gap: string;
  };
} = {
  SM: { icon: 'h-3 w-3', label: 'text-sm', amount: 'text-sm', gap: 'gap-2' },
  MD: { icon: 'h-[14px] w-[14px]', label: 'text-sm', amount: 'text-sm', gap: 'gap-2' },
};

const TAG_SIZES: {
  [key in NonNullable<TAccumulationLabelProps['size']>]: {
    box: string;
    label: string;
    amount: string;
    icon: string;
  };
} = {
  SM: { box: 'w-fit px-1 gap-4', label: 'text-xs', amount: 'text-base', icon: 'h-3 w-3' },
  MD: { box: 'w-fit px-2 gap-2', label: 'text-xs', amount: 'text-lg', icon: 'h-[14px] w-[14px]' },
};

/** Stands in for the wording when there is no room for it. */
const PLUS_SIGN = '+';

/**
 * Wallet accumulation label: how much the customer adds to the company wallet
 * with a purchase. Two designs, same data — `TAG` over product images and
 * `INLINE` inside summaries. Presentational on purpose: it takes an already
 * formatted amount and knows nothing about the page it sits on.
 */
const AccumulationLabel: FunctionComponent<TAccumulationLabelProps> = ({
  amount,
  tense = 'BEFORE',
  label = 'TEXT',
  variant = 'TAG',
  size = variant === 'INLINE' ? 'MD' : 'SM',
  background = 'bg-blue-100',
  color = 'text-blue-100',
  className,
}) => {
  const { t } = useTranslation();
  const isAfter = tense === 'AFTER';
  const isPlus = label === 'PLUS';

  if (variant === 'INLINE') {
    const inline = INLINE_SIZES[size];
    const sentence = isPlus
      ? PLUS_SIGN
      : isAfter
        ? t('common:wallet-accumulated-full', null, {
          fallback: 'Earned into your wallet',
        })
        : t('common:wallet-accumulates-full', null, {
          fallback: 'Earn into your wallet',
        });

    return (
      <div
        className={cx(
          'flex items-center justify-end gap-1 w-full',
          color,
          className
        )}
      >
        <span className={cx('flex items-center', inline.gap)}>
          <IconWallet className={cx(inline.icon, 'shrink-0')} />
          <span className={inline.label}>{sentence}</span>
        </span>
        <strong className={cx('font-bold whitespace-nowrap', inline.amount)}>
          {amount}
        </strong>
      </div>
    );
  }

  const sizing = TAG_SIZES[size];

  const wording = isPlus
    ? PLUS_SIGN
    : isAfter
      ? t('common:wallet-accumulated', null, { fallback: 'earned' })
      : t('common:wallet-accumulates', null, { fallback: 'earn' });


  return (
    <div
      className={cx(
        background,
        'flex flex-row gap-2 items-center',
        'p-1 px-2',
        'text-center text-white text-xs',
        //sizing.box,
        className
      )}
    >
      <IconWallet className={cx('text-white', sizing.icon)} />
      <div className="flex flex-row items-startjustify-center gap-1">
        <span className={cx('block font-normal w-fit')}>{wording}</span>
        <span className={cx('block w-full font-bold')}>
          {amount}
        </span>
      </div>
    </div>
  );
};

export default AccumulationLabel;
